'use client'

import { storeFutuExchange } from '@/shared/store/exchange'
import { OrderMethod } from '@/shared/types/defined'
import { convIntervalStr, convIntervalToResolution, getDecimalPlaces, GetKey } from '@/shared/utils/formatter'
import { useEffect, useRef, useCallback } from 'react'
import Datafeed from './datafeed'
import { storeFutuUserInfo } from '@/shared/store/userInfo'
import { unPnlPersent } from '@/shared/utils/maths'
import { Timezone } from '../lib/tradingview/charting_library'
import { ConvetDotSize } from '@/shared/utils/formatter'
import { PutDcaSetting } from '@/entities/dca/api/putDcaSetting'
import { useAlertifyStore } from '@/widget/alertify/store/alertify'
import useAlert from '@/shared/hooks/useAlert'


interface tvFutuOption {
    symbol: string
    interval: string
    locale: string
    ChartKey: string
    MaxCnt: number
    pricescale: number
    timeZone: Timezone
    ready: boolean
}

export default function ChartFuture() {
    const { userHavePosition, openOrders, algoOpenOrders, interval, timezone, setInterval, getAllOpenOrders } = storeFutuUserInfo()
    const { exSymbol, currnetPrice, getFutuExchangeInfo } = storeFutuExchange()
    const { addAlert } = useAlert();
    const currnetPriceRef = useRef(currnetPrice) // 초기 currnetPrice 값을 저장
    const exSymbolPriceTickSize = getFutuExchangeInfo(exSymbol)?.tickSize ?? '0'
    // const exSymbolPriceTickSize = ''

    const tvInfo = useRef<tvFutuOption>({
        symbol: exSymbol,
        interval: convIntervalToResolution(interval) || '60', // 기본값을 1시간으로 설정
        locale: 'en', // 한국어 번역 문제로 영어로 설정
        ChartKey: 'futuChartData',
        MaxCnt: 1500,
        pricescale: Math.pow(10, getDecimalPlaces(Number(exSymbolPriceTickSize))),
        timeZone: timezone,
        ready: false,
    })
    const tvRef = useRef<tvFutuComponentRef>(null)

    // 🔹 차트가 준비될 때까지 대기하는 유틸리티 함수
    const waitForChartReady = (callback: () => void) => {
        const checkChartReady = () => {
            if (tvRef.current && tvRef.current.isReady()) {
                callback()
            } else {
                setTimeout(checkChartReady, 100)
            }
        }
        checkChartReady()
    }

    const setFutuOpenOrder = (openOrder: Map<string, FutuAPI_GetusesrOpenOrders_Res>) => {
        if (openOrder === undefined) {
            return
        }

        if (!(openOrder instanceof Map)) {
            return
        }

        if (openOrder.size === 0) {
            tvRef?.current?.SetOpenOrder([])
            return
        }

        // console.log('orders : ', openOrder)

        const orders: CandleOpenOrder[] = Array.from(openOrder)
            .flatMap(([key, value]) => {
                if (value.type === 'LIMIT') {
                    return [{
                        orderid: value.orderId,
                        orderType: value.type,
                        posSide: value.positionSide || '',
                        price: value.price || '0',
                        symbol: value.symbol,
                        quantity: value.origQty || '',
                        sellbuy: value.side,
                    } as CandleOpenOrder]
                }

                if (
                    value.type === OrderMethod.STOP_MARKET ||
                    value.type === OrderMethod.TAKE_PROFIT_MARKET ||
                    value.type === OrderMethod.TAKE_PROFIT ||
                    value.type === OrderMethod.STOP
                    // || value.origType === OrderMethod.TRAILING_STOP_MARKET
                ) {
                    return [{
                        orderid: value.orderId,
                        orderType: value.type,
                        posSide: value.positionSide || '',
                        price: value.stopPrice || '0',
                        symbol: value.symbol,
                        quantity: value.origQty || '',
                        sellbuy: value.side,
                    } as CandleOpenOrder]
                }

                //
                if (value.type === OrderMethod.TRAILING_STOP_MARKET) {
                    return [{
                        orderid: value.orderId,
                        orderType: value.type,
                        posSide: value.positionSide || '',
                        price: value.activatePrice || '0',
                        symbol: value.symbol,
                        quantity: value.origQty || '',
                        sellbuy: value.side,
                    } as CandleOpenOrder]
                }

                // 기본 케이스 - 모든 조건에 해당하지 않는 경우
                return [{
                    orderid: value.orderId,
                    orderType: value.type,
                    posSide: value.positionSide || '',
                    price: value.price || value.stopPrice || value.activatePrice || '0',
                    symbol: value.symbol,
                    quantity: value.origQty || '',
                    sellbuy: value.side,
                } as CandleOpenOrder]
            })

        if (tvRef.current === null) {
            return
        }
        // console.log('orders : ', orders)
        tvRef.current.SetOpenOrder(orders)
    }

    // 포지션
    const setPosition = (position: Map<string, FutuAPI_GetPositionList_Res>) => {
        const chartPosition: CandleFutuPositionInfo[] = Array.from(position).map(([key, value]) => ({
            symbol: value.symbol,
            posSide: value.positionSide,
            price: value.entryPrice,
            amt: Math.abs(Number(value.positionAmt)).toString(),
            closepos: value.liquidationPrice,
        }))

        if (tvRef.current === null) {
            return
        }

        try {
            tvRef.current.SetPosition(chartPosition)

            // ticker - update 처리하자!!
        } catch (e) {
            console.log(e)
            if (tvRef.current !== undefined && tvRef.current !== null) {
                tvRef.current.SetPosition([])
            }
        }
    }

    // 포지션 가격 업데이트
    const updatePosition = (argsPrice: string) => {
        if(argsPrice === '0') return

        const longkey = GetKey(exSymbol, 'LONG')
        const shortkey = GetKey(exSymbol, 'SHORT')
        if (!(userHavePosition instanceof Map)) {
            console.log('map이 아닙니다. ')
            return
        }

        const longPosition = userHavePosition.get(longkey)
        if (longPosition !== undefined) {
            const [pnl, percent] = unPnlPersent(
                Number(longPosition.entryPrice),
                Number(argsPrice),
                Number(longPosition.positionAmt),
                longPosition.positionSide,
                longPosition.leverage
            )

            if (tvRef.current !== null) {
                tvRef.current.UpdatePosition('LONG', ConvetDotSize(String(pnl), 2), ConvetDotSize(String(percent), 2))
            }
        }

        const shortPosition = userHavePosition.get(shortkey)
        if (shortPosition !== undefined) {
            const [pnl, percent] = unPnlPersent(
                Number(shortPosition.entryPrice),
                Number(argsPrice),
                Number(shortPosition.positionAmt),
                shortPosition.positionSide,
                shortPosition.leverage
            )
            if (tvRef.current !== null) {
                tvRef.current.UpdatePosition('SHORT', ConvetDotSize(String(pnl), 2), ConvetDotSize(String(percent), 2))
            }
        }
    }

    // 변경 작업 !!
    const chagneSymbol =
        (futuSymbol: string, pricesalce: number) => {
            tvInfo.current.symbol = futuSymbol
            if (tvRef.current) {
                // 인터벌을 명시적으로 설정하여 "5달" 문제 해결
                const currentInterval = tvInfo.current.interval || '60' // 기본값을 1시간으로 설정
                tvRef.current.SetChangeSymbol(futuSymbol, currentInterval, pricesalce)
            }
        }


    // ChartCandleInt
    const chartCandleInt = (candle: string) => {
        if (tvInfo.current === undefined) {
            return
        }

        tvInfo.current.interval = candle
        setInterval(candle)
    }

    // 차트 라인 드래그로 주문 가격 변경 시 API 호출
    // 성공시 true, 실패시 false 반환 (라인 복원용)
    const handleModifyOrder = useCallback(async (orderId: number, newPrice: number): Promise<boolean> => {
        try {
            // getAllOpenOrders()에서 해당 주문 찾기
            const allOrders = getAllOpenOrders()
            if (!allOrders) return false

            // orderId로 주문 찾기 (clientOrderId와 매칭)
            let targetOrder: FutuAPI_GetusesrOpenOrders_Res | undefined
            allOrders.forEach((order) => {
                if (order.orderId === orderId) {
                    targetOrder = order
                }
            })

            if (!targetOrder) {
                console.error(`주문 정보를 찾을 수 없습니다.`)
                return false
            }

            const requestBody: PutDcaSettingReq = {
                id_ord: String(orderId),
                price: String(newPrice),
                symbol: targetOrder.symbol,
                position_side: targetOrder.positionSide as 'LONG' | 'SHORT',
                id_ex: '6', // 바이낸스 선물 거래소 코드
            }

            // API 호출
            const result = await PutDcaSetting(requestBody)

            // 주문 변경 실패 시 처리
            if (result.result_code !== '100') {
                addAlert({ message: result.msg })
                // 주문 변경 실패 시 원래 가격으로 복원
                return false
            }

            // 주문 변경 성공 시 새로운 가격으로 업데이트
            return true
        } catch (error) {
            addAlert({ message: '주문 변경 실패' })
            return false
        }
    }, [getAllOpenOrders, addAlert])

    useEffect(() => {
        const allOrders = getAllOpenOrders()
        if (allOrders) {
            // 🔹 차트가 준비될 때까지 대기
            waitForChartReady(() => {
                setFutuOpenOrder(allOrders)
            })
        }
    }, [openOrders, algoOpenOrders, getAllOpenOrders])

    useEffect(() => {
        // 🔹 차트가 준비될 때까지 대기
        waitForChartReady(() => {
            setPosition(userHavePosition)
            updatePosition(currnetPriceRef.current)
        })
    }, [userHavePosition])

    useEffect(() => {
        // 포지션 가격변동
        updatePosition(currnetPrice)
    }, [currnetPrice])

    useEffect(() => {
        const priceScale = Math.pow(10, getDecimalPlaces(Number(exSymbolPriceTickSize)))
        if (tvInfo.current === undefined) {
            tvInfo.current = {
                symbol: exSymbol,
                interval: convIntervalToResolution(interval),
                locale: 'en', // 한국어 번역 문제로 영어로 설정
                ChartKey: 'futuChartData',
                MaxCnt: 1500,
                pricescale: priceScale,
                timeZone: timezone,
                ready: false,
            }
        }
        // ref가 랜더링 되기전에 실행되는디?
        if (tvRef.current) {
            // console.log('exSymbol', exSymbol)
            chagneSymbol(exSymbol, priceScale)
        }
    }, [exSymbol, exSymbolPriceTickSize, tvRef])

    useEffect(() => {
        const priceScale = Math.pow(10, getDecimalPlaces(Number(exSymbolPriceTickSize)))
        tvInfo.current = {
            symbol: exSymbol,
            interval: convIntervalToResolution(interval) || '60', // 기본값을 1시간으로 설정
            locale: 'en', // 한국어 번역 문제로 영어로 설정
            ChartKey: 'futuChartData',
            MaxCnt: 1500,
            pricescale: priceScale,
            timeZone: timezone,
            ready: false,
        }
    }, [])

    return (
        <Datafeed
            Info={tvInfo.current}
            ref={tvRef}
            onChangeCandleINT={chartCandleInt}
            onModifyOrder={handleModifyOrder}
        />
    )
}
